import { Fragment, useState } from 'react';
import {
  DashboardMenuItem,
  Menu,
  MenuItemLink,
  useHasDashboard,
  useResourceDefinitions,
  usePermissions,
  useSidebarState,
  useCreatePath,
} from 'react-admin';
import { Collapse, List, ListItemButton, ListItemText } from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { MENU_LAYOUT, visibleGroups, withoutHiddenResources } from './menuConfig';
import { useRoleAccessState } from './PermissionGate';
import { resourceIcon } from './menuIcons';
import { useCompanyModules } from './ModuleGate';
import { moduleEnabledFor } from './modules';
import { sessionCompanyId } from '../authProvider';
import { useFeatureFlags } from './featureFlags/FeatureGate';
import { MyFleetBankMenuItem } from './userOffers/MyFleetBank';
import { FtlNotesMenuLabel } from './ftlNotes/FtlNotesMenuLabel';
import { OrderChangesMenuLabel } from './orderChanges/OrderChangesMenuLabel';
import { FEATURE_INTAKE_DEPARTMENTS, intakeSideAllowed } from './intake/intakeSides';
import { PublicationExceptionsMenuLabel } from './publications/PublicationExceptionsMenuLabel';
import {
  FEATURE_PUBLICATION_EXCEPTIONS,
  GRANT_PUBLICATION_EXCEPTION_APPROVE,
  useExceptionsSummary,
} from './publications/publicationExceptionsApi';

/**
 * Menu boczne.
 *
 * Port z FreightFusion (`admin/CustomMenu.jsx`). Stylowanie przeniesione
 * bez zmian — to samo zagęszczenie i te same rozmiary czcionek, żeby
 * powłoka wyglądała znajomo. Układ pozycji pochodzi z `menuConfig.ts`.
 *
 * Rysujemy wyłącznie zasoby zarejestrowane w `<Admin>`, więc menu nie może
 * pokazać linku do ekranu, którego jeszcze nie przenieśliśmy.
 */

/**
 * Etykieta pozycji zawija się zamiast ucinać wielokropkiem.
 *
 * Panel ma 180 px, a pozycja w grupie zaczyna tekst po wcięciu i ikonie —
 * „Parametry podpowiedzi” kończyło się jako „Parametry pod…” przy 1366 px
 * (weryfikacja na dev, 28.09.2026). react-admin rysuje etykietę jako
 * `Typography noWrap`, a MUI `MenuItem` ma `nowrap` — oba zdejmujemy.
 * Etykiety nie skracamy: tytuł ekranu bierze ją z menu (`menuLabel`).
 */
export const MENU_ITEM_WRAP_SX = {
  whiteSpace: 'normal',
  '& .MuiTypography-noWrap': {
    whiteSpace: 'normal',
    overflow: 'visible',
    textOverflow: 'clip',
    lineHeight: 1.25,
  },
} as const;

// Zagęszczenie pozycji — przeniesione ze źródła; do tego zawijanie etykiet.
const itemSx = {
  py: 0.5,
  minHeight: 36,
  fontSize: '0.8rem',
  '& .MuiListItemIcon-root': { minWidth: 32 },
  '& .MuiSvgIcon-root': { fontSize: '1.1rem' },
  '& .MuiTypography-root': { fontSize: '0.8rem' },
  '& .RaMenuItemLink-icon': { minWidth: 32 },
  ...MENU_ITEM_WRAP_SX,
};

const groupButtonSx = {
  ...itemSx,
  '& .MuiListItemText-primary': { fontSize: '0.8rem' },
};

const subItemIndent = (expanded: boolean) => ({
  paddingLeft: expanded ? 28 : 14,
  paddingTop: 4,
  paddingBottom: 4,
  minHeight: 32,
});

export const CustomMenu = () => {
  const definitions = useResourceDefinitions();
  // Adres pozycji liczy react-admin, nie my.
  //
  // Do 22.09.2026 stał tu prefiks ze zmiennej środowiskowej
  // (`REACT_APP_ADMIN_PREFIX`, domyślnie `/admin/`) — pamiątka po
  // FreightFusion, gdzie panel wisiał pod `/admin`. Ta instancja stoi
  // w katalogu głównym, więc KAŻDA pozycja menu prowadziła pod adres,
  // którego router nie zna, i cała aplikacja odpowiadała „Not Found”.
  // `useCreatePath` bierze przedrostek z `<Admin basename>`, więc link
  // jest poprawny i wtedy, gdy panel kiedyś zamieszka pod ścieżką.
  const createPath = useCreatePath();
  const { permissions } = usePermissions<string>();
  // Centrum ról (22.10.2026): menu przelicza się, gdy przyjdą uprawnienia
  // i ukryte pozycje roli z API (np. po zmianie wersji konta).
  useRoleAccessState();
  const [sidebarOpen] = useSidebarState();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  // Moduły spółek (T.01): ekran modułu wyłączonego w kontekście konta znika.
  const companies = useCompanyModules();
  // Pulpit jest stroną startową (`<Admin dashboard>`), ale menu z własnymi
  // pozycjami nie dokłada go samo — po przejściu do innego ekranu nie było
  // do niego drogi (lista komunikatów z 01.10.2026). Pozycja tylko wtedy,
  // gdy pulpit jest zarejestrowany.
  const hasDashboard = useHasDashboard();

  // Przełączniki funkcji (15.10.2026): ekran za wyłączonym przełącznikiem
  // znika z menu; zanim przyjdą (albo po błędzie odczytu) — też go nie ma.
  const features = useFeatureFlags();
  // Wnioski o wyjątek od bramki publikacji (fala B, 21.10.2026): czy konto
  // rozstrzyga wnioski, mówi serwer — uprawnienie bywa nadane imiennie.
  const exceptions = useExceptionsSummary(features?.flags[FEATURE_PUBLICATION_EXCEPTIONS] === true);
  const grants = exceptions?.canDecide ? [GRANT_PUBLICATION_EXCEPTION_APPROVE] : [];

  const visible = visibleGroups(
    MENU_LAYOUT,
    Object.keys(definitions),
    permissions,
    (module) => moduleEnabledFor(companies, module, null, sessionCompanyId()),
    (feature) => features?.flags[feature] === true,
    // Działy przyjęcia zleceń (fala B, 21.10.2026): bez przełącznika —
    // wspólne „Przyjęcie zleceń”, jak dotąd.
    (side) =>
      intakeSideAllowed(permissions, side, features?.flags[FEATURE_INTAKE_DEPARTMENTS] === true),
    grants,
  );
  // Profil wydania (25.10.2026, S-25.10-02): wyłączona paczka z opcją „ukryj
  // też z menu” chowa dotychczasowe ekrany przyjęcia zleceń — listę podaje API.
  const groups = withoutHiddenResources(visible, features?.hiddenMenu ?? []);

  const toggle = (title: string) =>
    setExpanded((s) => ({ ...s, [title]: !s[title] }));

  return (
    <Menu>
      {hasDashboard && <DashboardMenuItem primaryText="Pulpit" sx={itemSx} />}
      {groups.map((group) => {
        // Grupa bez tytułu to pozycje najwyższego poziomu, bez zwijania.
        if (!group.title) {
          return group.pozycje.map((p) => (
            <Fragment key={p.resource}>
              <MenuItemLink
                to={createPath({ resource: p.resource, type: 'list' })}
                // „Uwagi od FTL” z licznikiem otwartych (20.10.2026).
                primaryText={
                  p.resource === 'ftl-notes' ? (
                    <FtlNotesMenuLabel label={p.label} />
                  ) : p.resource === 'order-changes' ? (
                    // Zmiany zleceń w toku (fala B, B9, 21.10.2026).
                    <OrderChangesMenuLabel label={p.label} />
                  ) : (
                    p.label
                  )
                }
                leftIcon={resourceIcon(p.resource)}
                sx={itemSx}
                // Kontrakt `data-testid` paczki (S-22.10-02): pozycja menu — u nas zasób.
                data-testid={`wsp-menu-${p.resource}`}
              />
              {/* „Zlecenia mojej floty” pod „Zleceniami” (makieta FTL pkt 9.3,
                  przełącznik `bankMyFleetView`, 15.10.2026). */}
              {p.resource === 'user-offers' ? (
                <MyFleetBankMenuItem leftIcon={resourceIcon(p.resource)} sx={itemSx} />
              ) : null}
            </Fragment>
          ));
        }

        const open = expanded[group.title] ?? false;
        return (
          <div key={group.title}>
            <ListItemButton
              onClick={() => toggle(group.title as string)}
              sx={groupButtonSx}
              aria-expanded={open}
            >
              {sidebarOpen && (
                <>
                  <ListItemText primary={group.title} />
                  {open ? <ExpandLess /> : <ExpandMore />}
                </>
              )}
            </ListItemButton>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <List disablePadding>
                {group.pozycje.map((p) => (
                  <MenuItemLink
                    key={p.resource}
                    to={createPath({ resource: p.resource, type: 'list' })}
                    data-testid={`wsp-menu-${p.resource}`}
                    // „Wnioski o wyjątek” z licznikiem czekających (21.10.2026).
                    primaryText={
                      p.resource === 'publication-exceptions' ? (
                        <PublicationExceptionsMenuLabel label={p.label} summary={exceptions} />
                      ) : (
                        p.label
                      )
                    }
                    leftIcon={resourceIcon(p.resource)}
                    style={subItemIndent(sidebarOpen)}
                    sx={itemSx}
                  />
                ))}
              </List>
            </Collapse>
          </div>
        );
      })}
    </Menu>
  );
};

export default CustomMenu;
